import { useMemo } from 'react'

import { useSearchParams } from 'wouter'

import { useMst } from '#state/MstContext'
import { type Feature, reverseFlag } from '#util/featureFlags'

type FlagEnabled = boolean

interface SetFlagState {
  (enabled: FlagEnabled): void
}

/**
 * Read-only view of debug flags from the URL (`?debug=foo&debug=bar`)
 *
 * The returned `DebugFlags` object is a set of all feature flags set through
 * the url (not through the workspace)
 */
export function useDebugFlags() {
  const [searchParams] = useSearchParams()
  const values = searchParams.getAll('debug')
  const key = values.join('|')
  // oxlint-disable-next-line react/exhaustive-deps
  return useMemo(() => DebugFlags.fromValues(values), [key])
}

/**
 * `useState`-shaped hook for a single debug flag, backed by the URL. Setting a
 * param always replaces the state (so we don't clutter up the browser history)
 */
export function useDebugFlag(flag: string): [FlagEnabled, SetFlagState] {
  'use memo'
  const debugFlags = useDebugFlags()
  const [, setSearchParams] = useSearchParams()
  const enabled = debugFlags.isEnabled(flag)

  function setEnabled(nextEnabled: boolean) {
    if (enabled === nextEnabled) return

    setSearchParams(
      (params) => {
        const debugFlags = DebugFlags.fromValues(params.getAll('debug'))

        if (nextEnabled) {
          debugFlags.enable(flag)
        } else {
          debugFlags.disable(flag)
        }

        params.delete('debug')
        for (const enabledFlag of debugFlags) {
          params.append('debug', enabledFlag)
        }

        return params
      },
      { replace: true },
    )
  }

  return [enabled, setEnabled]
}

/**
 * `useState`-shaped hook for a workspace feature flag, with debug URL params
 * acting as per-session overrides. Reverse flags (`?debug=no-foo`) disable a
 * feature that is otherwise enabled for the workspace.
 */
export function useFeatureFlag(flag: Feature): [FlagEnabled, SetFlagState] {
  'use memo'

  const { currentOrg } = useMst()
  const [enabledOverride, setEnabledOverride] = useDebugFlag(flag)
  const [disabledOverride, setDisabledOverride] = useDebugFlag(
    reverseFlag(flag),
  )
  const enabledForWorkspace = Boolean(currentOrg?.betaFlags.includes(flag))
  const enabled = disabledOverride
    ? false
    : enabledOverride || enabledForWorkspace

  function setEnabled(nextEnabled: boolean) {
    if (enabled === nextEnabled) return

    if (enabledForWorkspace) {
      setDisabledOverride(!nextEnabled)
    } else {
      setEnabledOverride(nextEnabled)
    }
  }

  return [enabled, setEnabled]
}

/**
 * Visibility state for the dev toolbar (gotta be staff though)
 *
 * Combines the `?debug=toolbar` flag with the sticky `showDebugToolbar` user
 * preference
 *
 * `show`/`hide`/`toggle` write both, so closing the toolbar persists across
 * reloads
 */
export function useDebugToolbar() {
  'use memo'
  const { user } = useMst()
  const [toolbarEnabled, setToolbarEnabled] = useDebugFlag('toolbar')
  const isVisible = user.staff && (toolbarEnabled || shouldShowStickyToolbar())

  function shouldShowStickyToolbar() {
    return !!user.prefs.showDebugToolbar
  }

  function show() {
    setToolbarEnabled(true)
    user.updatePreferences({ showDebugToolbar: true })
  }

  function hide() {
    setToolbarEnabled(false)
    user.updatePreferences({ showDebugToolbar: false })
  }

  function toggle() {
    if (isVisible) {
      hide()
    } else {
      show()
    }
  }

  return {
    isVisible,
    show,
    hide,
    toggle,
  }
}

export class DebugFlags {
  private flags: Set<string>

  static fromValues(values: string[]) {
    return new DebugFlags(values)
  }

  constructor(flags: string[]) {
    this.flags = new Set()
    for (const flag of flags) this.enable(flag)
  }

  isEnabled(flag: string) {
    return this.flags.has(normalize(flag))
  }

  enable(flag: string) {
    const normalizedFlag = normalize(flag)
    if (normalizedFlag !== '') this.flags.add(normalizedFlag)
  }

  disable(flag: string) {
    this.flags.delete(normalize(flag))
  }

  isEmpty() {
    return this.flags.size === 0
  }

  [Symbol.iterator]() {
    return this.flags[Symbol.iterator]()
  }
}

function normalize(flag: string) {
  return flag.trim().toLowerCase()
}
